import React, { useState } from 'react';
import { base44 } from '@/api/base44Client';
import { Loader2, Play, CheckCircle2, AlertTriangle, Sparkles } from 'lucide-react';

// Runs the one-off tidy-up across every customer record: fills blank phone
// numbers and delivery addresses from the customer's most recent order, and
// tidies names / postcodes / address lines everywhere. Safe to run more than
// once — an existing value is never overwritten.
export default function CustomerDetailBackfillPanel() {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const run = async () => {
    setRunning(true);
    setError('');
    try {
      const res = await base44.functions.invoke('backfillCustomerDetails', {});
      const data = res?.data || {};
      if (data.error) throw new Error(data.error);
      setResult(data);
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'The tidy-up could not be completed.');
    }
    setRunning(false);
  };

  return (
    <div className="bg-[#111115] border border-white/5 p-5">
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div className="flex-1 min-w-[280px]">
          <h3 className="text-white font-black text-base uppercase flex items-center gap-2.5">
            <Sparkles className="w-4 h-4 text-primary" /> Customer Details Tidy-Up
          </h3>
          <p className="text-white/40 text-xs mt-2 leading-relaxed max-w-2xl">
            Fills in any missing phone number and delivery address on customer accounts and CRM records
            from that customer's most recent order, and tidies names, postcodes and address lines across
            accounts, orders and trade records. Anything already saved is left exactly as it is, so this
            is safe to run as often as you like.
          </p>
        </div>
        <button
          onClick={run}
          disabled={running}
          className="flex-shrink-0 bg-primary hover:bg-primary/90 text-white font-bold text-[10px] tracking-wider px-4 py-2.5 transition-colors disabled:opacity-40 flex items-center gap-1.5"
        >
          {running ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />}
          {running ? 'RUNNING…' : 'RUN NOW'}
        </button>
      </div>

      {error && (
        <p className="mt-4 flex items-center gap-2 text-red-400 text-xs bg-red-400/10 border border-red-400/20 px-3 py-2">
          <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" /> {error}
        </p>
      )}

      {result && !error && (
        <div className="mt-4 border-t border-white/5 pt-4">
          <p className="flex items-center gap-2 text-emerald-400 text-xs font-bold">
            <CheckCircle2 className="w-3.5 h-3.5" /> {result.summary}
          </p>
          {result.notable?.length > 0 && (
            <div className="mt-3">
              <p className="text-white/30 text-[10px] font-bold tracking-widest mb-2">DETAILS FILLED IN</p>
              <ul className="space-y-1">
                {result.notable.map((line, i) => (
                  <li key={i} className="text-white/60 text-xs flex items-start gap-2">
                    <span className="text-primary mt-1.5 w-1 h-1 rounded-full bg-primary flex-shrink-0" />
                    {line}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}